import { t, type Translations } from './i18n/i18n';
import { fetchOffers } from '../screens/shop/shop-api';

/**
 * The occasional speech-bubble over the Shop tab: a short call to action, shown for 5 seconds, at most once every
 * 25 minutes. Which line it says depends on where the offer window is:
 *   - right after a reset (the window changed since the last tip): the next 3 tips announce fresh offers;
 *   - in the last hour before the window ends: 2 tips about the offers expiring;
 *   - otherwise: one of 10 general CTAs, never the same line twice in a row.
 * State lives in localStorage (best effort; private mode falls back to memory for the session).
 */

export const SHOP_TIP_KEY = 'dic:shopTip';
export const SHOP_TIP_INTERVAL_MS = 25 * 60 * 1000;
export const SHOP_TIP_SHOW_MS = 5000;
/** Don't greet the player the instant the game opens. */
export const SHOP_TIP_FIRST_DELAY_MS = 60_000;
export const RESET_TIPS = 3;
export const EXPIRY_TIPS = 2;
export const EXPIRY_WINDOW_MS = 60 * 60 * 1000;
const CHECK_EVERY_MS = 30_000;

export const GENERIC_TIP_KEYS = Array.from({ length: 10 }, (_, i) => `shop.tip.generic.${i + 1}`) as (keyof Translations)[];
export const RESET_TIP_KEYS = Array.from({ length: RESET_TIPS }, (_, i) => `shop.tip.reset.${i + 1}`) as (keyof Translations)[];
export const EXPIRY_TIP_KEYS = Array.from({ length: EXPIRY_TIPS }, (_, i) => `shop.tip.expiry.${i + 1}`) as (keyof Translations)[];

export interface ShopTipState {
  /** When a tip was last shown (ms). */
  lastAt: number;
  /** The offer window the counters below belong to; null before the first tip ever. */
  win: number | null;
  /** Reset announcements already shown in `win`. */
  reset: number;
  /** Expiry warnings already shown in `win`. */
  expiry: number;
  /** Index of the last general CTA, so it is not repeated back to back. */
  generic: number;
}

export const EMPTY_SHOP_TIP_STATE: ShopTipState = { lastAt: 0, win: null, reset: 0, expiry: 0, generic: -1 };

export interface TipChoice {
  key: keyof Translations;
  params?: Record<string, string | number>;
  next: ShopTipState;
}

/**
 * Pure: the tip to show now and the state to store after it. `windowIndex` / `windowEndsAt` describe the live offer
 * window (null when unknown — only general CTAs are possible then). `rand` picks the general CTA.
 */
export function chooseTip(
  state: ShopTipState,
  window: { index: number; endsAt: number } | null,
  now: number,
  rand: () => number = Math.random,
): TipChoice {
  let s: ShopTipState = { ...state, lastAt: now };
  if (window) {
    if (s.win === null) {
      // First tip ever: the player has not seen a "reset", so there is nothing to announce.
      s = { ...s, win: window.index, reset: RESET_TIPS, expiry: 0 };
    } else if (s.win !== window.index) {
      s = { ...s, win: window.index, reset: 0, expiry: 0 };
    }
    if (s.reset < RESET_TIPS) {
      return { key: RESET_TIP_KEYS[s.reset], next: { ...s, reset: s.reset + 1 } };
    }
    const left = window.endsAt - now;
    if (left > 0 && left < EXPIRY_WINDOW_MS && s.expiry < EXPIRY_TIPS) {
      return { key: EXPIRY_TIP_KEYS[s.expiry], params: { min: Math.max(1, Math.round(left / 60_000)) }, next: { ...s, expiry: s.expiry + 1 } };
    }
  }
  let idx = Math.floor(rand() * GENERIC_TIP_KEYS.length);
  if (idx === s.generic) idx = (idx + 1) % GENERIC_TIP_KEYS.length;
  return { key: GENERIC_TIP_KEYS[idx], next: { ...s, generic: idx } };
}

let memory: ShopTipState = { ...EMPTY_SHOP_TIP_STATE };

export function loadShopTipState(): ShopTipState {
  try {
    const raw = localStorage.getItem(SHOP_TIP_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<ShopTipState>;
      return {
        lastAt: Number(p.lastAt) || 0,
        win: typeof p.win === 'number' ? p.win : null,
        reset: Number(p.reset) || 0,
        expiry: Number(p.expiry) || 0,
        generic: typeof p.generic === 'number' ? p.generic : -1,
      };
    }
    // Storage works and holds nothing: genuinely a first run.
    return { ...EMPTY_SHOP_TIP_STATE };
  } catch {
    // Storage unavailable or corrupt — fall back to the in-memory copy.
  }
  return { ...memory };
}

export function saveShopTipState(state: ShopTipState): void {
  memory = { ...state };
  try {
    localStorage.setItem(SHOP_TIP_KEY, JSON.stringify(state));
  } catch {
    // The in-memory copy still covers this session.
  }
}

export interface ShopTipOptions {
  /** The Shop tab button the bubble hangs over (it is appended inside it, so tapping the bubble opens the shop). */
  button: HTMLElement;
  /** True while the Shop is already on screen — no point advertising it then. */
  isShopOpen: () => boolean;
  now?: () => number;
}

/** Starts the schedule. Returns a teardown that also removes a bubble that is still showing. */
export function mountShopTip(opts: ShopTipOptions): () => void {
  const now = opts.now ?? Date.now;
  const startedAt = now();
  let disposed = false;
  let bubble: HTMLElement | null = null;
  let hideTimer: number | null = null;
  let busy = false;

  function hide(): void {
    if (hideTimer !== null) window.clearTimeout(hideTimer);
    hideTimer = null;
    bubble?.remove();
    bubble = null;
  }

  function show(text: string): void {
    hide();
    const el = document.createElement('span');
    el.className = 'shop-tip';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = text;
    opts.button.appendChild(el);
    bubble = el;
    // Keep the bubble on screen: it is centred over the tab, which can sit near an edge on a phone.
    const r = el.getBoundingClientRect();
    const overflowRight = r.right - (window.innerWidth - 8);
    const overflowLeft = 8 - r.left;
    const shift = overflowRight > 0 ? -overflowRight : overflowLeft > 0 ? overflowLeft : 0;
    if (shift !== 0) el.style.setProperty('--tip-shift', `${shift}px`);
    hideTimer = window.setTimeout(() => {
      el.classList.add('shop-tip--out');
      hideTimer = window.setTimeout(hide, 250);
    }, SHOP_TIP_SHOW_MS);
  }

  async function check(): Promise<void> {
    if (disposed || busy || document.hidden || opts.isShopOpen()) return;
    const t0 = now();
    if (t0 - startedAt < SHOP_TIP_FIRST_DELAY_MS) return;
    const state = loadShopTipState();
    if (state.lastAt > 0 && t0 - state.lastAt < SHOP_TIP_INTERVAL_MS) return;
    busy = true;
    try {
      let win: { index: number; endsAt: number } | null = null;
      try {
        const res = await fetchOffers();
        const endsAt = new Date(res.windowEndsAt).getTime();
        if (typeof res.windowIndex === 'number' && Number.isFinite(endsAt)) win = { index: res.windowIndex, endsAt };
      } catch {
        // No window info: a general CTA is still fine.
      }
      if (disposed) return;
      const choice = chooseTip(state, win, now());
      saveShopTipState(choice.next);
      show(t(choice.key, choice.params));
    } finally {
      busy = false;
    }
  }

  const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
  return () => {
    disposed = true;
    window.clearInterval(timer);
    hide();
  };
}
